import { useState, useEffect } from "react";
import { ShoppingBag, Menu, X, Search as SearchIcon, User, LogOut, LayoutDashboard, ChevronDown } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { CATEGORIES } from "../../data/products";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { useCurrency } from "@/lib/currency";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { cart, openDrawer } = useCart();
  const { currency, setCurrency, supported, label } = useCurrency();
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const cartCount = cart?.total_qty || 0;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e?.preventDefault?.();
    if (searchQ.trim().length === 0) return;
    setSearchOpen(false);
    navigate(`/search?q=${encodeURIComponent(searchQ.trim())}`);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-shadow duration-300 bg-white ${
        scrolled ? "border-b border-neutral-200 shadow-[0_1px_0_rgba(0,0,0,0.04)]" : "border-b border-transparent"
      }`}
    >
      <div className="hidden md:block bg-black text-white text-[11px] tracking-[0.2em] uppercase">
        <div className="max-w-[1400px] mx-auto px-6 py-2 flex justify-between items-center">
          <span className="flex items-center gap-3">
            <span>B2B Wholesale · Pharmaceutical-grade aesthetic supplies</span>
          </span>
          <span className="flex gap-6 items-center">
            <div className="relative">
              <button
                onClick={() => setCurrencyOpen((v) => !v)}
                className="inline-flex items-center gap-1 hover:opacity-70"
                data-testid="topbar-currency-btn"
                aria-label="Select currency"
              >
                <span className="font-bold">{currency}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {currencyOpen && (
                <div className="absolute right-0 top-full mt-1 min-w-[180px] bg-white text-black border border-neutral-200 shadow-lg z-50 normal-case tracking-normal" data-testid="topbar-currency-menu">
                  <div className="px-3 py-2 border-b border-neutral-200 text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">Display currency</div>
                  {supported.map((c) => (
                    <button
                      key={c}
                      onClick={() => { setCurrency(c); setCurrencyOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 ${c === currency ? "font-bold bg-neutral-50" : ""}`}
                      data-testid={`currency-opt-${c}`}
                    >
                      {c} <span className="text-neutral-500 text-xs ml-1">— {c === currency ? label : ""}</span>
                    </button>
                  ))}
                  <div className="px-3 py-2 border-t border-neutral-200 text-[10px] text-neutral-500">Display only · Charged in USD</div>
                </div>
              )}
            </div>
            <Link to="/journal" className="hover:opacity-70" data-testid="topbar-blog">Journal</Link>
            <a href="mailto:hello@wholesalefillers.world" className="hover:opacity-70" data-testid="topbar-contact">Contact</a>
          </span>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-5 flex items-center justify-between gap-8">
        <Link to="/" className="flex items-baseline gap-2 select-none" data-testid="brand-logo">
          <span className="font-heading text-2xl font-thin tracking-tight">Discount</span>
          <span className="font-heading text-2xl font-black tracking-tight">Fillers</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7 text-sm">
          {CATEGORIES.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="font-medium text-neutral-700 hover:text-black transition-colors"
              data-testid={`nav-${c.slug}`}
            >
              {c.name}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setSearchOpen((v) => !v)}
            className="hidden sm:flex items-center justify-center w-10 h-10 hover:bg-neutral-100 transition-colors"
            data-testid="header-search-btn"
            aria-label="Search"
          >
            <SearchIcon className="w-4 h-4" />
          </button>

          {/* Account menu */}
          <div className="relative">
            {user ? (
              <button
                onClick={() => setUserMenuOpen((v) => !v)}
                className="flex items-center justify-center w-10 h-10 hover:bg-neutral-100"
                data-testid="header-account-btn"
                aria-label="Account"
              >
                <User className="w-4 h-4" />
              </button>
            ) : (
              <Link to="/login" className="flex items-center justify-center w-10 h-10 hover:bg-neutral-100" data-testid="header-login-btn" aria-label="Login">
                <User className="w-4 h-4" />
              </Link>
            )}
            {userMenuOpen && user && (
              <div className="absolute right-0 top-full mt-2 w-56 border border-neutral-200 bg-white shadow-lg z-10" data-testid="user-menu">
                <div className="px-4 py-3 border-b border-neutral-200">
                  <div className="text-xs text-neutral-500">Signed in as</div>
                  <div className="text-sm font-bold truncate">{user.email}</div>
                </div>
                <Link to="/account" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-neutral-50" data-testid="menu-account">
                  <User className="w-3.5 h-3.5 inline mr-2" /> My account
                </Link>
                {user.role === "admin" && (
                  <Link to="/admin" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-neutral-50" data-testid="menu-admin">
                    <LayoutDashboard className="w-3.5 h-3.5 inline mr-2" /> Admin dashboard
                  </Link>
                )}
                <button
                  onClick={async () => { setUserMenuOpen(false); await logout(); navigate("/"); }}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-neutral-50 border-t border-neutral-200"
                  data-testid="header-logout"
                >
                  <LogOut className="w-3.5 h-3.5 inline mr-2" /> Sign out
                </button>
              </div>
            )}
          </div>

          <button
            onClick={openDrawer}
            className="relative flex items-center justify-center w-10 h-10 hover:bg-neutral-100"
            data-testid="header-cart-btn"
            aria-label="Cart"
          >
            <ShoppingBag className="w-4 h-4" />
            {cartCount > 0 && (
              <span className="absolute top-1 right-1 bg-black text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full" data-testid="cart-count">
                {cartCount}
              </span>
            )}
          </button>
          <button
            className="lg:hidden flex items-center justify-center w-10 h-10 hover:bg-neutral-100"
            onClick={() => setOpen(!open)}
            data-testid="header-menu-btn"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-neutral-200 bg-white" data-testid="search-bar">
          <form onSubmit={submitSearch} className="max-w-[1400px] mx-auto px-6 py-4 flex items-center gap-3">
            <SearchIcon className="w-5 h-5 text-neutral-400" />
            <input
              autoFocus
              type="text"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search products, brands, categories…"
              className="flex-1 bg-transparent text-base focus:outline-none placeholder:text-neutral-400"
              data-testid="search-input"
            />
            <button type="submit" className="bg-black text-white px-5 py-2 text-sm font-medium hover:bg-neutral-800" data-testid="search-submit">
              Search
            </button>
            <button type="button" onClick={() => setSearchOpen(false)} className="w-9 h-9 flex items-center justify-center hover:bg-neutral-100" aria-label="Close search">
              <X className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}

      {open && (
        <div className="lg:hidden border-t border-neutral-200 bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4">
            <form onSubmit={submitSearch} className="flex items-center gap-2 border border-neutral-300 px-3 py-2 mb-4">
              <SearchIcon className="w-4 h-4 text-neutral-400" />
              <input
                type="text"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search…"
                className="flex-1 bg-transparent text-sm focus:outline-none"
                data-testid="mobile-search-input"
              />
            </form>
            <div className="flex flex-col gap-1">
              {CATEGORIES.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  onClick={() => setOpen(false)}
                  className="text-left py-2 font-medium text-neutral-800 hover:text-black border-b border-neutral-100"
                  data-testid={`mobile-nav-${c.slug}`}
                >
                  {c.name}
                </Link>
              ))}
              <Link to="/journal" onClick={() => setOpen(false)} className="text-left py-2 font-medium text-neutral-800 hover:text-black border-b border-neutral-100">Journal</Link>
              {!user ? (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="text-left py-2 font-medium text-neutral-800 hover:text-black border-b border-neutral-100">Sign in</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="text-left py-2 font-medium text-neutral-800 hover:text-black border-b border-neutral-100">Apply for account</Link>
                </>
              ) : (
                <>
                  <Link to="/account" onClick={() => setOpen(false)} className="text-left py-2 font-medium text-neutral-800 hover:text-black border-b border-neutral-100">My account</Link>
                  {user.role === "admin" && (
                    <Link to="/admin" onClick={() => setOpen(false)} className="text-left py-2 font-medium text-neutral-800 hover:text-black border-b border-neutral-100">Admin</Link>
                  )}
                  <button onClick={async () => { setOpen(false); await logout(); navigate("/"); }} className="text-left py-2 font-medium text-neutral-800 hover:text-black">Sign out</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
